/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useState, useEffect } from "react";
import { getDateRange } from "../utils/utils";
import { format, startOfWeek, addDays  } from "date-fns";
const today = new Date();
const currentWeekStart = startOfWeek(today, { weekStartsOn: 0 }); // Sunday this week
const currentWeekEnd = addDays(currentWeekStart, 6); // Saturday this week
const formattedStartTime = format(currentWeekStart, "yyyy-MM-dd'T'00:00:00xxx");
const formattedEndTime = format(currentWeekEnd, "yyyy-MM-dd'T'23:59:59xxx");
import { AccountDetails, DEFAULT_FEATURE_MAP, FeatureMap, FeatureStatus, ImpersonationDetails } from "../utils/types";

interface Global {
  readonly userExists: boolean;
  readonly setUserExists: React.Dispatch<React.SetStateAction<boolean>>;
  readonly calendarsData: any[] | null;
  readonly setCalendarsData: React.Dispatch<React.SetStateAction<any[] | null>>;
  readonly insightsAvailable: boolean;
  readonly setInsightsAvailable: React.Dispatch<React.SetStateAction<boolean>>;
  readonly timeRange: string;
  readonly setTimeRange: React.Dispatch<React.SetStateAction<string>>;
  readonly startEndTime: { start_time: string; end_time: string };
  readonly setStartEndTime: React.Dispatch<
    React.SetStateAction<{ start_time: string; end_time: string }>
  >;
  readonly dateRange: { startDate: Date; endDate: Date; }; // New state  
  readonly setDateRange: React.Dispatch<  
    React.SetStateAction<{ startDate: Date; endDate: Date; }>  
  >;
  readonly selectedPreset: string;
readonly setSelectedPreset: React.Dispatch<React.SetStateAction<string>>;  
  readonly startEndMeetingTime: { start_time: string; end_time: string };  // New state
  readonly setStartEndMeetingTime: React.Dispatch<
  React.SetStateAction<{ start_time: string; end_time: string }>
>;  // New set method
  readonly calendar: any | null;
  readonly setCalendar: React.Dispatch<React.SetStateAction<any | null>>;
  readonly resolveConflictingMeeting:
  
    | {
        id: string;
        isSelected: boolean;
      }[]
    | null;
  readonly setResolveConflictingMeeting: React.Dispatch<
    React.SetStateAction<
      | {
          id: string;
          isSelected: boolean;
        }[]
      | null
    >
  >;
  readonly focusTimeMeeting: any | null;
  readonly setFocusTimeMeeting: React.Dispatch<
    React.SetStateAction<any | null>
  >;
  readonly roleId: string;
  readonly setRoleId: React.Dispatch<React.SetStateAction<string>>;
  readonly adminScreen: boolean;
  readonly setAdminScreen: React.Dispatch<React.SetStateAction<boolean>>;
  readonly accountsReports: boolean;
  readonly setAccountsReports: React.Dispatch<React.SetStateAction<boolean>>;
  readonly pipState: PipState;
  readonly setPipState: React.Dispatch<React.SetStateAction<PipState>>;
  readonly reportingSearch: string;
  readonly setReportingSearch: React.Dispatch<React.SetStateAction<string>>;
  readonly prevPath: string | null;
  readonly setPrevPath: React.Dispatch<React.SetStateAction<string | null>>;
  readonly userTeamData: {userTeamDataMessage: string, showTeamTab: boolean};
  readonly setUserTeamData: React.Dispatch<React.SetStateAction<{userTeamDataMessage: string, showTeamTab: boolean}>>;
  readonly featureMap: FeatureMap;
  readonly setFeatureMap: React.Dispatch<React.SetStateAction<FeatureMap>>;

  readonly featureStatus: FeatureStatus;
  readonly setFeatureStatus: React.Dispatch<React.SetStateAction<FeatureStatus>>;
  readonly accountDetails: AccountDetails | null;
  readonly setAccountDetails: React.Dispatch<React.SetStateAction<AccountDetails | null>>;
  readonly impersonationDetails: ImpersonationDetails | null;
  readonly setImpersonationDetails: React.Dispatch<React.SetStateAction<ImpersonationDetails | null >>;
  readonly impersonationReady: boolean;
  readonly setImpersonationReady: React.Dispatch<React.SetStateAction<boolean>>;
  readonly spaceList: { label: string; value: string }[];
  readonly setSpaceList: React.Dispatch<React.SetStateAction<{ label: string; value: string }[]>>;
  readonly selectedView: "org" | "spaces";
  readonly setSelectedView: React.Dispatch<React.SetStateAction<"org" | "spaces">>;
  readonly selectedSpace: string[];
  readonly setSelectedSpace: React.Dispatch<React.SetStateAction<string[]>>;
}

interface PipState {
  meetingId: string | null;
  triggered: boolean;
  videoElement: HTMLVideoElement | null;
  resumeTabIndex: number | null;
  videoFileId: string | null;
  tempVideoFileId: string | null;
  resumeTime: number | null;
  isTeamMeeting: boolean;
}

interface GlobalContextProviderProps {
  children: JSX.Element[] | JSX.Element;
}

export const GlobalContext = createContext<Global>({
  userExists: false,
  setUserExists: () => {},
  calendarsData: null,
  setCalendarsData: () => {},
  timeRange: "currentWeek",
  setTimeRange: () => {},
  startEndTime: { start_time: "", end_time: "" },
  setStartEndTime: () => {},
  dateRange: { startDate: new Date(), endDate: new Date() }, // Default value for new state  
  setDateRange: () => {},
  selectedPreset: "currentWeek", // Default value
  setSelectedPreset: () => {}, 
  startEndMeetingTime: { start_time: formattedStartTime, end_time: formattedEndTime }, // Default value for new state
  setStartEndMeetingTime: () => {},  // Default function
  calendar: null,
  setCalendar: () => {},
  resolveConflictingMeeting: null,
  setResolveConflictingMeeting: () => {},
  focusTimeMeeting: null,
  setFocusTimeMeeting: () => {},
  insightsAvailable: false,
  setInsightsAvailable: () => {},
  roleId: "",
  setRoleId: () => {},
  adminScreen: false,
  setAdminScreen:() => {},
  accountsReports: false,
  setAccountsReports:() => {},
  pipState: {
    meetingId: null,
    triggered: false,
    videoElement: null,
    resumeTabIndex: null,
    videoFileId: null,
    tempVideoFileId: null,
    resumeTime: null,
    isTeamMeeting: false,
  },
  setPipState: () => {},
  reportingSearch:"",
  setReportingSearch:() => {},
  prevPath: null,
  setPrevPath: () => {},
  userTeamData: {userTeamDataMessage: "", showTeamTab: true},
  setUserTeamData: ()=> {},
  featureMap: DEFAULT_FEATURE_MAP,
  setFeatureMap: () => {},

  featureStatus: "idle",
  setFeatureStatus: () => {},
  accountDetails: null,
  setAccountDetails: () => {},
  impersonationDetails: null,
  setImpersonationDetails: () => {},
  impersonationReady: false,
  setImpersonationReady: () => {},
  spaceList: [],
  setSpaceList: () => {},
  selectedView: "spaces",
  setSelectedView: () => {},
  selectedSpace: [],
  setSelectedSpace: () => {},
});

export const GlobalContextProvider: React.FC<GlobalContextProviderProps> = ({
  children,
}) => {
  const [userExists, setUserExists] = useState(false);
  const [calendarsData, setCalendarsData] = useState<any[] | null>(null);
   const [insightsAvailable, setInsightsAvailable] = useState(false);
  const [timeRange, setTimeRange] = useState("currentWeek");
  const [startEndTime, setStartEndTime] = useState({
    start_time: "",
    end_time: "",
  });
  const [dateRange, setDateRange] = useState({  
    startDate: currentWeekStart,
    endDate: currentWeekEnd, 
  });  
  const [selectedPreset, setSelectedPreset] = useState<string>("currentWeek");
  const [startEndMeetingTime, setStartEndMeetingTime] = useState({
    start_time: formattedStartTime,
    end_time: formattedEndTime,
  });  // State for startEndMeetingTime
  const [calendar, setCalendar] = useState<any | null>(null);
  const [resolveConflictingMeeting, setResolveConflictingMeeting] = useState<
    | {
        id: string;
        isSelected: boolean;
      }[]
    | null
  >(null);
  const [focusTimeMeeting, setFocusTimeMeeting] = useState<any | null>(null);
  const [roleId, setRoleId] = useState<string>("");
  const [adminScreen, setAdminScreen] = useState<boolean>(false);
  const [accountsReports, setAccountsReports] = useState<boolean>(false);
  const [pipState, setPipState] = useState<PipState>({
    meetingId: null,
    triggered: false,
    videoElement: null,
    resumeTabIndex: null,
    videoFileId: null,
    tempVideoFileId: null,
    resumeTime: null,
    isTeamMeeting: false,
  });
  const [ reportingSearch, setReportingSearch] = useState<string>("");

  useEffect(() => {
    if (timeRange !== "") {
      const result = getDateRange(timeRange);
      setStartEndTime({
        start_time: result.start_time,
        end_time: result.end_time,
      });
    }
  }, [timeRange]);
  const [prevPath, setPrevPath] = useState<string | null>(null);
  const [userTeamData, setUserTeamData] = useState<{ userTeamDataMessage: string; showTeamTab: boolean }>({ userTeamDataMessage: "", showTeamTab: true });

  const [featureMap, setFeatureMap] = useState<FeatureMap>(DEFAULT_FEATURE_MAP);
  const [featureStatus, setFeatureStatus] = useState<FeatureStatus>("idle");
  const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(null);
  const [impersonationDetails, setImpersonationDetails] = useState<ImpersonationDetails | null>(null);
  const [impersonationReady, setImpersonationReady] = useState(false);
  const [spaceList, setSpaceList] = useState<{ label: string; value: string }[]>([]);
  const [selectedView, setSelectedView] = useState<"org" | "spaces">(() => {
    const saved = sessionStorage.getItem("selectedView");
    return (saved === "org" || saved === "spaces") ? saved : "spaces";
  });
  const [selectedSpace, setSelectedSpace] = useState<string[]>(() => {
    const saved = sessionStorage.getItem("selectedSpace");
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    sessionStorage.setItem("selectedView", selectedView);
  }, [selectedView]);

  useEffect(() => {
    sessionStorage.setItem("selectedSpace", JSON.stringify(selectedSpace));
  }, [selectedSpace]);

  return (
    <GlobalContext.Provider
      value={{
        userExists,
        setUserExists,
        calendarsData,
        setCalendarsData,
        timeRange,
        setTimeRange,
        startEndTime,
        setStartEndTime,
        dateRange, 
        setDateRange,
        selectedPreset,
        setSelectedPreset,
        startEndMeetingTime,  // Added new state value
        setStartEndMeetingTime,  // Added new set function
        calendar,
        setCalendar,
        resolveConflictingMeeting,
        setResolveConflictingMeeting,
        focusTimeMeeting,
        setFocusTimeMeeting,
        insightsAvailable,
        setInsightsAvailable,
        roleId,
        setRoleId,
        adminScreen,
        setAdminScreen,
        accountsReports,
        setAccountsReports,
        pipState,
        setPipState,
        reportingSearch,
        setReportingSearch,
        prevPath,
        setPrevPath,
        userTeamData,
        setUserTeamData,
        featureMap,
        setFeatureMap,
        featureStatus,
        setFeatureStatus,
        accountDetails,
        setAccountDetails,
        impersonationDetails,
        setImpersonationDetails,
        impersonationReady,
        setImpersonationReady,
        spaceList,
        setSpaceList,
        selectedView,
        setSelectedView,
        selectedSpace,
        setSelectedSpace,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
